// @flow
import * as React from 'react';
import { Route, Switch } from 'react-router';
import ListPage from 'esm-web-pro/containers/pages/ListPage';
import NetworkPage from 'esm-web-pro/containers/pages/network/NetworkPage';
import {
    FormattedMessage, IntlShape, injectIntl, defineMessages,
} from 'react-intl';
import { Trash } from 'grommet-icons';
import ActionsColumn from 'speed-js-react-web/src/components/grommet2/tables/columns/Action/ActionsColumn';
import ColorColumn from 'speed-js-react-web/src/components/grommet2/tables/columns/ColorColumn';
import { ThemeContext } from 'styled-components';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import EditIcon from 'esm-web-pro/components/icons/EditIcon';
import CableModelDashboard from 'esm-web-pro/containers/pages/administration/infrastructure/CableModelDashboard';
import useCreatableField from 'esm-web-pro/hooks/useCreatableField';

type Props = {
    intl: IntlShape,
}

const getContentTypes = ({ original: { content_types: contentTypes } }) => (
    contentTypes ? contentTypes.map((v) => `${v.app_label}.${v.model}`).join(', ') : ''
);

const getEditObjectLabel = (initials) => (
    <FormattedMessage
        id="adminpage-networks-edit-provider"
        defaultMessage="Edit Provider {provider}"
        values={{ provider: initials?.entity?.name }}
    />
);

const messages = defineMessages({
    providers: {
        id: 'adminpage-networks-providers',
        defaultMessage: 'Providers',
    },
});

const NetworkRoutes = (networkProps: Props) => {
    const { intl: { formatMessage } } = networkProps;
    const [extraSchema, formikRef, orgCreator] = useCreatableField('entity', 'uuid', {
        panelId: 'create-provider-from-field-panel',
        resourceArguments: [],
        resourceName: 'organisation',
        multi: false,
        title: (
            <FormattedMessage
                id="entities-createperson"
                defaultMessage="Create person"
            />
        ),
    });

    const formProps = React.useMemo(() => ({
        extraSchema,
        forwardRef: formikRef,
    }), [extraSchema, formikRef]);

    return (
        <>
            <Switch>
                <Route
                    path="/administration/infrastructure/providers"
                    render={(props) => (
                        <>
                            <ListPage
                                {...props}
                                resourceName="infrastructureProvider"
                                permissions={['networks.view_infrastructureprovider',
                                    'networks.delete_infrastructureprovider',
                                    'networks.change_infrastructureprovider',
                                    'networks.add_infrastructureprovider']}
                                title={formatMessage(messages.providers)}
                                columns={[
                                    {
                                        Header: <FormattedMessage
                                            id="adminpage-network-name"
                                            defaultMessage="Name"
                                        />,
                                        accessor: 'entity.name',
                                    },
                                    {
                                        Header: <FormattedMessage
                                            id="adminpage-network-infrastructureprovider-color"
                                            defaultMessage="Color"
                                        />,
                                        accessor: 'color',
                                        Cell: ColorColumn,
                                    },
                                    {
                                        Header: <FormattedMessage
                                            id="adminpage-network-description"
                                            defaultMessage="Description"
                                        />,
                                        accessor: 'description',
                                    },
                                    {
                                        Header: <FormattedMessage
                                            id="adminpage-network-content-types"
                                            defaultMessage="Content types"
                                        />,
                                        to: '/administration/nomenclatures/types/',
                                        resourceMethod: 'contenttypes',
                                        accessor: 'content_types',
                                        minWidth: 70,
                                        Cell: getContentTypes,
                                    },
                                    {
                                        actions: [{
                                            label: formatMessage(actionMessages.edit),
                                            to: '/administration/infrastructure/providers/update/{uuid}',
                                            type: 'link',
                                            icon: <EditIcon />,
                                        }, {
                                            label: formatMessage(actionMessages.delete),
                                            resourceName: 'infrastructureProvider',
                                            accessor: 'uuid',
                                            type: 'delete',
                                            icon: <Trash />,
                                            nameAccessor: 'entity.name',
                                        }],
                                        Cell: ActionsColumn,
                                        maxWidth: 160,
                                    },
                                ]}
                                newObjectUrl="/administration/infrastructure/providers/new"
                                newObjectLabel={(
                                    <FormattedMessage
                                        id="adminpage-networks-new-provider"
                                        defaultMessage="New Provider"
                                    />
                                )}
                                editObjectLabel={getEditObjectLabel()}
                                editObjectNestedInitials
                                editObjectInitialsCallback={(initials) => {
                                    const flatInitials = { ...initials };
                                    flatInitials.allowed_objects = initials.allowed_objects.map((allowedObject) => {
                                        allowedObject.content_types = allowedObject.content_types
                                            .map((contentType) => contentType.id);
                                        return allowedObject;
                                    });
                                    Object.keys(initials).forEach((key) => {
                                        if (typeof initials[key] === 'object' && initials[key].id) {
                                            flatInitials[key] = initials[key].uuid;
                                        } else {
                                            flatInitials[key] = initials[key];
                                        }
                                    });
                                    return flatInitials;
                                }}
                                newObjectFormProps={formProps}
                                editObjectFormProps={formProps}
                            />
                            {orgCreator}
                        </>
                    )}
                />
                <Route
                    path="/administration/infrastructure/cable-models"
                    component={CableModelDashboard}
                />
            </Switch>
        </>
    );
};

const NetworkMap = (
    <Route
        path="/infrastructure/map/:mapIdentity?/:versionType?"
        render={() => (
            <ThemeContext.Extend
                value={{ box: { extend: { position: 'relative' } } }}
            >
                <NetworkPage />
            </ThemeContext.Extend>
        )}
    />
);

export { NetworkMap };

export default injectIntl(NetworkRoutes);
